/**
 * VuePrint v1.6.3
 * Copyright (c) 2024 Jak Guru LLC
 * Vueprint a commerical work product released under the MIT License
 * and is provided as-is with no warranty or guarantee of support.
 * Documentation: https://jakguru.github.io/vueprint/
 * 
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import { EventEmitter } from 'events';
import { DateTime } from 'luxon';

var __accessCheck = (obj, member, msg) => {
  if (!member.has(obj))
    throw TypeError("Cannot " + msg);
};
var __privateGet = (obj, member, getter) => {
  __accessCheck(obj, member, "read from private field");
  return getter ? getter.call(obj) : member.get(obj);
};
var __privateAdd = (obj, member, value) => {
  if (member.has(obj))
    throw TypeError("Cannot add the same private member more than once");
  member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
};
var __privateSet = (obj, member, value, setter) => {
  __accessCheck(obj, member, "write to private field");
  setter ? setter.call(obj, value) : member.set(obj, value);
  return value;
};
var __privateMethod = (obj, member, method) => {
  __accessCheck(obj, member, "access private method");
  return method;
};
var _bus, _timeoutId, _running, _onTick, onTick_fn, _crontabContraints, crontabContraints_get, _aliases, aliases_get, _parseField, parseField_fn;
const _MiliCron = class _MiliCron {
  /**
   * Creates a new instance of the MiliCron class.
   * @constructor
   * @param {boolean} [autostart=false] - Whether to automatically start the cron daemon upon instantiation.
   * @returns {MiliCron} - A new instance of the MiliCron class.
   */
  constructor(autostart) {
    __privateAdd(this, _onTick);
    __privateAdd(this, _bus, void 0);
    __privateAdd(this, _timeoutId, void 0);
    __privateAdd(this, _running, false);
    __privateSet(this, _bus, new EventEmitter({ captureRejections: true }));
    __privateGet(this, _bus).setMaxListeners(Infinity);
    if (autostart === true) {
      this.start();
    }
  }
  /**
   * Starts the cron daemon if it is not already running.
   * @function
   * @returns {void}
   */
  start() {
    if (__privateGet(this, _running)) {
      return;
    }
    __privateMethod(this, _onTick, onTick_fn).call(this);
  }
  /**
   * Stops the cron daemon if it is running.
   * @function
   * @returns {void}
   */
  stop() {
    if (__privateGet(this, _timeoutId)) {
      clearTimeout(__privateGet(this, _timeoutId));
    }
    __privateSet(this, _running, false);
  }
  /**
   * Stops the cron daemon if it is running and then starts it again.
   * @function
   * @returns {void}
   */
  restart() {
    this.stop();
    this.start();
  }
  /**
   * Returns a boolean indicating whether the cron daemon is currently running.
   * @readonly
   * @returns {boolean} - A boolean indicating whether the cron daemon is currently running.
   */
  get running() {
    return __privateGet(this, _running);
  }
  $on(event, cb) {
    __privateGet(this, _bus).on(event, cb);
  }
  $once(event, cb) {
    __privateGet(this, _bus).once(event, cb);
  }
  $off(event, cb) {
    __privateGet(this, _bus).off(event, cb);
  }
  /**
   * Removes all listeners for the specified event or all events if no event is specified.
   * @param {string} [event] - The name of the event to remove all listeners from.
   * @returns {void}
   * @see {@link events!EventEmitter.removeAllListeners | EventEmitter.removeAllListeners}
   */
  $clear(event) {
    if (event) {
      __privateGet(this, _bus).removeAllListeners(event);
    } else {
      __privateGet(this, _bus).removeAllListeners();
    }
  }
  /**
   * Determines whether the given crontab expression matches the given date and time.
   * @param {string} crontab - The crontab expression to check.
   * @param {DateTime} now - The date and time to check against the crontab expression.
   * @returns {boolean} - A boolean indicating whether the crontab expression matches the given date and time.
   */
  crontabMatchesDateTime(crontab, now) {
    return _MiliCron.crontabMatchesDateTime(crontab, now);
  }
  /**
   * Parses the given cron expression and returns either a `DateTime` object representing the next time the cron expression will match, or a `CronTabObject` containing the parsed cron expression.
   * @param {string} cronExpression - The cron expression to parse.
   * @param {DateTime} [now] - The current date and time to use as a reference when calculating the next match time. If not provided, the current date and time will be used.
   * @throws {Error} - Throws an error if the cron expression is so invalid that it cannot be recognized, or if the `now` parameter is not a valid `DateTime` object.
   * @returns {DateTime | CronTabObject} - A `DateTime` object representing the next time the cron expression will match, or a `CronTabObject` containing the parsed cron expression.
   */
  getParsedCronExpression(cronExpression, now) {
    return _MiliCron.getParsedCronExpression(cronExpression, now);
  }
  /**
   * Check if the crontab expression matches the current time
   * @param crontab A crontab expression, crontab alias or unix timestamp
   * @param now A DateTime object representing the current time
   * @returns If the crontab expression matches the current time
   */
  static crontabMatchesDateTime(crontab, now) {
    if (!now.isValid) {
      return false;
    }
    try {
      const cronTabObject = this.getParsedCronExpression(crontab, now);
      if (cronTabObject instanceof DateTime) {
        return cronTabObject >= now.minus({ milliseconds: 9 }) && cronTabObject <= now.plus({ milliseconds: 9 });
      }
      return [
        cronTabObject.millisecondly.has(now.millisecond),
        cronTabObject.secondly.has(now.second),
        cronTabObject.minutely.has(now.minute),
        cronTabObject.hourly.has(now.hour),
        cronTabObject.dayOfMonthly.has(now.day),
        cronTabObject.monthly.has(now.month),
        cronTabObject.dayOfWeekly.has(now.weekday)
      ].every((value) => value === true);
    } catch {
      return false;
    }
  }
  /**
   * Parses the given cron expression and returns either a `DateTime` object representing the next time the cron expression will match, or a `CronTabObject` containing the parsed cron expression.
   * @param {string} cronExpression - The cron expression to parse.
   * @param {DateTime} [now] - The current date and time to use as a reference when calculating the next match time. If not provided, the current date and time will be used.
   * @throws {Error} - Throws an error if the cron expression is so invalid that it cannot be recognized, or if the `now` parameter is not a valid `DateTime` object.
   * @returns {DateTime | CronTabObject} - A `DateTime` object representing the next time the cron expression will match, or a `CronTabObject` containing the parsed cron expression.
   */
  static getParsedCronExpression(cronExpression, now) {
    if (!now) {
      now = DateTime.now().toUTC();
    } else if (!now.isValid) {
      throw new Error("Invalid DateTime object");
    }
    if (cronExpression.startsWith("@")) {
      const expression = __privateGet(this, _aliases, aliases_get).expressions[cronExpression];
      if (expression) {
        return this.getParsedCronExpression(expression);
      }
    }
    const crontabParts = cronExpression.replace(/\s+/g, " ").split(" ").map((part) => part.trim());
    if (crontabParts.length === 1) {
      return DateTime.fromSeconds(parseInt(crontabParts[0]));
    }
    if (crontabParts.length > 7) {
      throw new Error("Invalid crontab expression");
    }
    while (crontabParts.length < 7) {
      crontabParts.unshift("0");
    }
    const [cronMil, cronSec, cronMin, cronHour, cronDayOfMonth, cronMonth, cronDayOfWeek] = crontabParts;
    const cronTabObject = {
      millisecondly: __privateMethod(this, _parseField, parseField_fn).call(this, "millisecond", cronMil, __privateGet(this, _crontabContraints, crontabContraints_get).millisecondly, now),
      secondly: __privateMethod(this, _parseField, parseField_fn).call(this, "second", cronSec, __privateGet(this, _crontabContraints, crontabContraints_get).secondly, now),
      minutely: __privateMethod(this, _parseField, parseField_fn).call(this, "minute", cronMin, __privateGet(this, _crontabContraints, crontabContraints_get).minutely, now),
      hourly: __privateMethod(this, _parseField, parseField_fn).call(this, "hour", cronHour, __privateGet(this, _crontabContraints, crontabContraints_get).hourly, now),
      dayOfMonthly: __privateMethod(this, _parseField, parseField_fn).call(this, "dayOfMonth", cronDayOfMonth, __privateGet(this, _crontabContraints, crontabContraints_get).dayOfMonthly, now),
      monthly: __privateMethod(this, _parseField, parseField_fn).call(this, "month", cronMonth, __privateGet(this, _crontabContraints, crontabContraints_get).monthly, now),
      dayOfWeekly: __privateMethod(this, _parseField, parseField_fn).call(this, "dayOfWeek", cronDayOfWeek, __privateGet(this, _crontabContraints, crontabContraints_get).dayOfWeekly, now)
    };
    return cronTabObject;
  }
};
_bus = new WeakMap();
_timeoutId = new WeakMap();
_running = new WeakMap();
_onTick = new WeakSet();
onTick_fn = function() {
  __privateSet(this, _running, true);
  const now = DateTime.now().toUTC();
  const eventsWithListeners = __privateGet(this, _bus).eventNames();
  eventsWithListeners.filter((e) => "error" !== e).forEach((eventName) => {
    if (_MiliCron.crontabMatchesDateTime(eventName.toString(), now)) {
      __privateGet(this, _bus).emit(eventName);
    }
  });
  __privateSet(this, _timeoutId, setTimeout(__privateMethod(this, _onTick, onTick_fn).bind(this), 10));
};
_crontabContraints = new WeakSet();
crontabContraints_get = function() {
  const constraints = {
    millisecondly: {
      min: 0,
      max: 999,
      chars: [],
      validChars: /^[,*\d/-]+$/
    },
    secondly: {
      min: 0,
      max: 59,
      chars: [],
      validChars: /^[,*\d/-]+$/
    },
    minutely: {
      min: 0,
      max: 59,
      chars: [],
      validChars: /^[,*\d/-]+$/
    },
    hourly: {
      min: 0,
      max: 23,
      chars: [],
      validChars: /^[,*\d/-]+$/
    },
    dayOfMonthly: {
      min: 1,
      max: 31,
      chars: ["L"],
      validChars: /^[?,*\dL/-]+$/
    },
    monthly: {
      min: 1,
      max: 12,
      chars: [],
      validChars: /^[,*\d/-]+$/
    },
    dayOfWeekly: {
      min: 0,
      max: 7,
      chars: ["L"],
      validChars: /^[?,*\dL#/-]+$/
    }
  };
  Object.freeze(constraints);
  return constraints;
};
_aliases = new WeakSet();
aliases_get = function() {
  const aliases = {
    expressions: {
      "@yearly": "0 0 0 0 1 1 *",
      "@monthly": "0 0 0 0 1 * *",
      "@weekly": "0 0 0 0 * * 0",
      "@daily": "0 0 0 0 * * *",
      "@hourly": "0 0 0 * * * *"
    },
    month: {
      JAN: 1,
      FEB: 2,
      MAR: 3,
      APR: 4,
      MAY: 5,
      JUN: 6,
      JUL: 7,
      AUG: 8,
      SEP: 9,
      OCT: 10,
      NOV: 11,
      DEC: 12
    },
    daysOfWeek: {
      SUN: 0,
      MON: 1,
      TUE: 2,
      WED: 3,
      THU: 4,
      FRI: 5,
      SAT: 6
    }
  };
  Object.freeze(aliases);
  return aliases;
};
_parseField = new WeakSet();
parseField_fn = function(field, value, constraints, now) {
  const ret = /* @__PURE__ */ new Set();
  const aliases = __privateGet(this, _aliases, aliases_get)[field];
  switch (field) {
    case "month":
    case "dayOfWeek":
      value = value.replace(/[a-z]{3}/gi, (match) => {
        match = match.toUpperCase();
        if (!aliases) {
          return "X";
        } else if (typeof aliases[match] !== "undefined") {
          return aliases[match].toString();
        } else {
          return "X";
        }
      });
      break;
  }
  if (value === "X" || !constraints.validChars.test(value)) {
    return ret;
  }
  if (value.includes("*")) {
    value = value.replace(/\*/g, constraints.min + "-" + constraints.max);
  } else if (value.includes("?")) {
    value = value.replace(/\?/g, constraints.min + "-" + constraints.max);
  }
  const atoms = value.split(",");
  atoms.forEach((atom) => {
    if (atom.includes("-")) {
      if (atom.includes("/")) {
        if (atom.split("/").length !== 2) {
          return;
        }
        const [range, step] = atom.split("/");
        const [start, end] = range.split("-");
        const startNum = parseInt(start);
        const endNum = parseInt(end);
        const stepNum = parseInt(step);
        if (stepNum <= 0) {
          return;
        }
        if (startNum > endNum) {
          return;
        }
        for (let i = startNum; i <= endNum; i += stepNum) {
          ret.add(i);
        }
      } else {
        const [start, end] = atom.split("-");
        const startNum = parseInt(start);
        const endNum = parseInt(end);
        if (startNum > endNum) {
          return;
        }
        for (let i = startNum; i <= endNum; i++) {
          ret.add(i);
        }
      }
    } else if (atom.includes("/")) {
      if (atom.split("/").length !== 2) {
        return;
      }
      const [start, step] = atom.split("/");
      const startNum = parseInt(start);
      const stepNum = parseInt(step);
      if (stepNum <= 0) {
        return;
      }
      if (startNum > constraints.max) {
        return;
      }
      for (let i = startNum; i <= constraints.max; i += stepNum) {
        ret.add(i);
      }
    } else if (atom.includes("#")) {
      const [dayOfWeek, weekOfMonth] = atom.split("#");
      const dayOfWeekNum = parseInt(dayOfWeek);
      const weekOfMonthNum = parseInt(weekOfMonth);
      if (dayOfWeekNum > constraints.max) {
        return;
      }
      const firstDayOfMonth = DateTime.fromObject({
        year: now.year,
        month: now.month,
        day: 1
      });
      const firstDayOfMonthDayOfWeek = firstDayOfMonth.weekday;
      const firstDayOfWeek = DateTime.fromObject({
        year: now.year,
        month: now.month,
        day: 1 + (dayOfWeekNum - firstDayOfMonthDayOfWeek)
      });
      const nthDayOfWeek = firstDayOfWeek.plus({
        weeks: weekOfMonthNum - 1
      });
      if (nthDayOfWeek.month !== now.month) {
        return;
      }
      ret.add(nthDayOfWeek.day);
    } else if (atom.includes("L")) {
      if (atom.split("/").length !== 2) {
        return;
      }
      const [start, step] = atom.split("/");
      const startNum = parseInt(start);
      const stepNum = parseInt(step);
      if (stepNum <= 0) {
        return;
      }
      if (startNum > constraints.max) {
        return;
      }
      for (let i = startNum; i <= constraints.max; i += stepNum) {
        ret.add(i);
      }
    } else {
      const num = parseInt(atom);
      if (num >= constraints.min && num <= constraints.max) {
        ret.add(num);
      }
    }
  });
  if (field === "millisecond") {
    const current = [...ret];
    current.forEach((num) => {
      const toAddMin = num - 9 < constraints.min ? constraints.min : num - 9;
      const toAddMax = num + 9 > constraints.max ? constraints.max : num + 9;
      for (let i = toAddMin; i <= toAddMax; i++) {
        ret.add(i);
      }
    });
  }
  if (field === "dayOfWeek") {
    if (ret.has(7)) {
      ret.add(0);
    }
    if (ret.has(0)) {
      ret.add(7);
    }
  }
  return ret;
};
__privateAdd(_MiliCron, _crontabContraints);
__privateAdd(_MiliCron, _aliases);
/**
 * Convert the crontab expression into a Set of numbers which represent possible matches
 * @param field The crontab field we are parsing, for additional constraints
 * @param value The current value of the field
 * @param constraints The general constraints for the field
 * @returns A set of numbers representing the possible values of the field
 */
__privateAdd(_MiliCron, _parseField);
let MiliCron = _MiliCron;

const milicron = {
  __proto__: null,
  MiliCron: MiliCron
};

export { MiliCron as M, milicron as m };
//# sourceMappingURL=vueprint.5d216d52.mjs.map
